import { Component, forwardRef, Input } from '@angular/core'
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms'

interface NumberFormInputConfig {
  min?: number
  max?: number
  step?: number
  ui?: {
    prefix?: string
    suffix?: string
    locale?: 'en-US' | 'de-DE' | 'fr-FR' // supported locales
    thousandSeparator?: string
    decimalSeparator?: string
  }
}

/**
 * @description
 * Displays a number input. Default input for `int` and `float` type fields.
 *
 * @docsCategory custom-input-components
 * @docsPage default-inputs
 */
@Component({
  selector: 'vdr-number-form-input2',
  templateUrl: './number-form-input2.component.html',
  styleUrls: ['./number-form-input2.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NumberFormInput2Component),
      multi: true
    }
  ]
})
export class NumberFormInput2Component implements ControlValueAccessor {
  @Input() config: NumberFormInputConfig
  @Input() readonly = false
  @Input() disabled = false

  value: number | null = null
  onChange: (value: any) => void = () => {}
  onTouched: () => void = () => {}

  // Add new property for displayed value
  displayValue = ''

  private get locale(): string {
    return this.config?.ui?.locale || 'de-DE'
  }

  private get thousandSeparator(): string {
    return this.config?.ui?.thousandSeparator || '.'
  }

  private get decimalSeparator(): string {
    return this.config?.ui?.decimalSeparator || '.'
  }

  writeValue(value: number): void {
    this.value = value
    this.updateDisplayValue()
  }

  registerOnChange(fn: any): void {
    this.onChange = fn
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled
  }

  // Add method to format number
  private updateDisplayValue(): void {
    if (this.value === null || this.value === undefined) {
      this.displayValue = ''
      return
    }

    // Convert number to string with proper decimal places
    const parts = this.value.toString().split('.')

    // Format integer part with thousand separator
    const integerPart = parts[0].replace(
      /\B(?=(\d{3})+(?!\d))/g,
      this.thousandSeparator
    )

    // Handle decimal part if exists
    if (parts.length > 1) {
      this.displayValue = `${integerPart}${this.decimalSeparator}${parts[1]}`
    } else {
      this.displayValue = integerPart
    }
  }

  onInputChange(event: Event): void {
    const input = event.target as HTMLInputElement
    // Remove thousand separators and normalize decimal separator
    let value = input.value
      .replace(new RegExp(`\\${this.thousandSeparator}`, 'g'), '')
      .replace(new RegExp(`\\${this.decimalSeparator}`, 'g'), '.')

    // Remove any remaining non-numeric characters except decimal point
    value = value.replace(/[^\d.]/g, '')

    // Ensure only one decimal point
    const parts = value.split('.')
    if (parts.length > 2) {
      value = parts[0] + '.' + parts.slice(1).join('')
    }

    if (value) {
      let numValue = Number(value)

      // If value exceeds max, set it to max
      if (this.config?.max !== undefined && numValue >= this.config.max) {
        numValue = this.config.max
        // Update input value immediately to show max
        input.value = this.config.max.toString()
      }
      // Apply min constraint
      if (this.config?.min !== undefined && numValue < this.config.min) {
        numValue = this.config.min
      }

      this.value = numValue
      this.updateDisplayValue()
      this.onChange(this.value)
    } else {
      this.value = null
      this.displayValue = ''
      this.onChange(null)
    }
  }

  onKeyPress(event: KeyboardEvent): void {
    const input = event.target as HTMLInputElement
    const currentValue = input.value
      .replace(new RegExp(`\\${this.thousandSeparator}`, 'g'), '')
      .replace(new RegExp(`\\${this.decimalSeparator}`, 'g'), '.')

    // Check if adding the new digit would exceed max
    if (this.config?.max !== undefined) {
      const proposedValue = currentValue + event.key
      const numValue = Number(proposedValue)
      if (!isNaN(numValue) && numValue >= this.config.max) {
        // Prevent the keypress and set to max value
        event.preventDefault()
        this.value = this.config.max
        this.updateDisplayValue()
        this.onChange(this.value)
        return
      }
    }

    const allowedKeys = [
      'Backspace',
      'Delete',
      'Tab',
      'Enter',
      'Escape',
      'ArrowLeft',
      'ArrowRight',
      this.decimalSeparator
    ]

    if (
      allowedKeys.includes(event.key) ||
      // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
      (event.ctrlKey === true &&
        ['a', 'c', 'v', 'x'].includes(event.key.toLowerCase()))
    ) {
      return
    }

    // Block non-numeric input
    if (!/^\d$/.test(event.key)) {
      event.preventDefault()
    }
  }
}
